import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [detail, setDetail] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep trying up to the limit
      }
      attempts.current += 1;
      if (attempts.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <main className="pay-result-wrap">
      <div className="pay-result soft-card">
        {state === "polling" && (
          <>
            <Loader2 size={44} className="spin" color="var(--gold-ink)" />
            <h1 className="pay-result-title">Confirming your contribution…</h1>
            <p className="pay-result-sub">A quiet moment while we confirm with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="pay-result-icon success"><CheckCircle2 size={40} /></span>
            <h1 className="pay-result-title">Thank you for your Dana</h1>
            <p className="pay-result-sub">
              {detail?.amount ? `Your contribution of €${Number(detail.amount).toFixed(2)}` : "Your contribution"}
              {detail?.recurring ? " per month" : ""}
              {detail?.path ? ` toward the ${detail.path} path` : ""} has been received with deep gratitude.
              A confirmation will follow by email.
            </p>
            <Link to="/contacts" className="btn-gold" style={{ marginTop: "1.5rem" }}>
              <Heart size={16} /> Return to Contacts
            </Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <span className="pay-result-icon neutral"><Loader2 size={40} /></span>
            <h1 className="pay-result-title">Still processing</h1>
            <p className="pay-result-sub">
              Your payment is taking a little longer than usual to confirm. If Stripe charged you,
              it will complete shortly — feel free to check your email.
            </p>
            <Link to="/contacts" className="btn-ghost" style={{ marginTop: "1.5rem" }}>Return to Contacts</Link>
          </>
        )}
        {state === "error" && (
          <>
            <span className="pay-result-icon error"><XCircle size={40} /></span>
            <h1 className="pay-result-title">We couldn't confirm this payment</h1>
            <p className="pay-result-sub">No charge may have been made. You are welcome to try again.</p>
            <Link to="/contacts" className="btn-ghost" style={{ marginTop: "1.5rem" }}>Return to Contacts</Link>
          </>
        )}
      </div>
    </main>
  );
};

export const PaymentCancel = () => (
  <main className="pay-result-wrap">
    <div className="pay-result soft-card">
      <span className="pay-result-icon neutral"><XCircle size={40} /></span>
      <h1 className="pay-result-title">Contribution not completed</h1>
      <p className="pay-result-sub">
        Your checkout was cancelled and no payment was made. Whenever you feel ready, your Dana is always welcome.
      </p>
      <Link to="/contacts" className="btn-gold" style={{ marginTop: "1.5rem" }}>
        <Heart size={16} /> Return to Contacts
      </Link>
    </div>
  </main>
);

export default PaymentSuccess;
